<template>
  <v-container fluid class="artikel-upload-container">
    <v-card>
      <v-card-title class="d-flex align-center bg-blue-darken-1 text-white">
        <v-icon class="mr-3" size="large">mdi-database-import</v-icon>
        <span class="text-h5">Artikel-Katalog Upload</span>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert type="info" variant="tonal" class="mb-4">
          <v-icon start>mdi-information</v-icon>
          Kopiere die Daten aus Excel und füge sie hier ein. Format: Code CMMF, Description, Code Commercial, GTIN/EAN, Familie, Family Description, Eligible, Paletten-Nr, Gewicht
        </v-alert>

        <!-- Excel Daten Textarea -->
        <v-textarea
          v-model="rawData"
          label="Excel-Daten (Tab-getrennt)"
          variant="outlined"
          rows="10"
          placeholder="Füge hier die Daten aus Excel ein (STRG+V)"
          :disabled="isProcessing"
          class="mb-4"
        >
          <template v-slot:prepend-inner>
            <v-icon color="success">mdi-table</v-icon>
          </template>
        </v-textarea>

        <!-- Parse Button -->
        <v-btn
          color="success"
          variant="elevated"
          :loading="isProcessing"
          :disabled="!rawData || isProcessing"
          @click="parseData"
          class="mb-4"
        >
          <v-icon start>mdi-code-json</v-icon>
          Daten analysieren
        </v-btn>

        <!-- Vorschau Tabelle -->
        <v-expand-transition>
          <div v-if="parsedRows.length > 0">
            <v-divider class="my-4"></v-divider>

            <div class="d-flex align-center mb-3">
              <h3 class="text-h6">Vorschau</h3>
              <v-spacer></v-spacer>
              <v-chip color="success" variant="elevated">
                {{ selectedRows.length }} / {{ parsedRows.length }} ausgewählt
              </v-chip>
            </div>

            <div class="mb-3">
              <v-btn
                variant="text"
                size="small"
                color="success"
                @click="selectAll"
              >
                <v-icon start size="small">mdi-checkbox-marked-outline</v-icon>
                Alle auswählen
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                color="grey"
                @click="deselectAll"
              >
                <v-icon start size="small">mdi-checkbox-blank-outline</v-icon>
                Keine auswählen
              </v-btn>
            </div>

            <v-data-table
              v-model="selectedRows"
              :headers="tableHeaders"
              :items="parsedRows"
              item-value="index"
              show-select
              density="compact"
              class="elevation-1"
              :items-per-page="10"
            >
              <template v-slot:[`item.paletten_nr`]="{ item }">
                <v-chip size="small" color="primary">{{ item.paletten_nr }}</v-chip>
              </template>
              <template v-slot:[`item.gewicht_kg`]="{ item }">
                <span>{{ item.gewicht_kg || '—' }}</span>
              </template>
            </v-data-table>

            <v-divider class="my-4"></v-divider>

            <!-- Upload Button -->
            <div class="d-flex justify-end">
              <v-btn
                color="primary"
                variant="elevated"
                size="large"
                :loading="isUploading"
                :disabled="selectedRows.length === 0 || isUploading"
                @click="uploadData"
              >
                <v-icon start>mdi-upload</v-icon>
                {{ selectedRows.length }} Artikel hochladen
              </v-btn>
            </div>
          </div>
        </v-expand-transition>

        <!-- Ergebnis Anzeige -->
        <v-expand-transition>
          <v-alert
            v-if="uploadResult"
            :type="uploadResult.success ? 'success' : 'error'"
            variant="tonal"
            class="mt-4"
          >
            <v-icon start>{{ uploadResult.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            {{ uploadResult.message }}
            <div v-if="uploadResult.details" class="mt-2">
              <small>Erfolgreich: {{ uploadResult.details.success }}</small><br>
              <small>Fehler: {{ uploadResult.details.errors }}</small>
            </div>
          </v-alert>
        </v-expand-transition>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { API_BASE_URL } from '@/config';

const toast = useToast();

const rawData = ref('');
const parsedRows = ref([]);
const selectedRows = ref([]);
const isProcessing = ref(false);
const isUploading = ref(false);
const uploadResult = ref(null);

const tableHeaders = [
  { title: 'Code CMMF', key: 'code_cmmf', sortable: true },
  { title: 'Description', key: 'description_cmmf', sortable: true },
  { title: 'Code Commercial', key: 'code_commercial', sortable: true },
  { title: 'GTIN/EAN', key: 'gtin_ean', sortable: true },
  { title: 'Familie', key: 'famille_n', sortable: true },
  { title: 'Family Desc', key: 'family_lev1_description', sortable: false },
  { title: 'Eligible', key: 'eligible', sortable: true },
  { title: 'Palette', key: 'paletten_nr', sortable: true },
  { title: 'Gewicht', key: 'gewicht_kg', sortable: true }
];

const parseData = () => {
  isProcessing.value = true;
  uploadResult.value = null;

  try {
    const lines = rawData.value.split('\n').filter(line => line.trim());
    const rows = [];

    lines.forEach((line, index) => {
      const columns = line.split('\t');

      if (columns.length >= 8) {
        // Paletten-Nr extrahieren (z.B. "Pallet n° 5" -> "5")
        let palettenNr = columns[7] ? columns[7].trim() : '';
        const palettenMatch = palettenNr.match(/(?:Pallet n° |Palette |Pallet )(\d+)/i);
        if (palettenMatch) {
          palettenNr = palettenMatch[1];
        }

        // Gewicht (optional, kann leer sein)
        const gewicht = columns[8] ? parseFloat(columns[8].trim()) : null;

        rows.push({
          index: index,
          code_cmmf: columns[0]?.trim() || '',
          description_cmmf: columns[1]?.trim() || '',
          code_commercial: columns[2]?.trim() || '',
          gtin_ean: columns[3]?.trim() || '',
          famille_n: columns[4]?.trim() || '',
          family_lev1_description: columns[5]?.trim() || '',
          eligible: columns[6]?.trim() || '',
          paletten_nr: palettenNr,
          gewicht_kg: gewicht
        });
      }
    });

    parsedRows.value = rows;
    selectedRows.value = rows.map(r => r.index);
    toast.success(`${rows.length} Zeilen erfolgreich analysiert!`);
  } catch (error) {
    console.error('Fehler beim Parsen:', error);
    toast.error('Fehler beim Analysieren der Daten');
  } finally {
    isProcessing.value = false;
  }
};

const selectAll = () => {
  selectedRows.value = parsedRows.value.map(r => r.index);
};

const deselectAll = () => {
  selectedRows.value = [];
};

const uploadData = async () => {
  isUploading.value = true;
  uploadResult.value = null;

  try {
    const dataToUpload = parsedRows.value.filter(row =>
      selectedRows.value.includes(row.index)
    );

    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'upload_artikel_katalog',
        data: dataToUpload
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      uploadResult.value = {
        success: true,
        message: response.data.message,
        details: response.data.details
      };
      toast.success(response.data.message);

      // Reset nach erfolgreichem Upload
      rawData.value = '';
      parsedRows.value = [];
      selectedRows.value = [];
    } else {
      uploadResult.value = {
        success: false,
        message: response.data.message
      };
      toast.error(response.data.message);
    }
  } catch (error) {
    console.error('Upload-Fehler:', error);
    uploadResult.value = {
      success: false,
      message: 'Netzwerkfehler beim Hochladen'
    };
    toast.error('Netzwerkfehler beim Hochladen');
  } finally {
    isUploading.value = false;
  }
};
</script>

<style scoped>
.artikel-upload-container {
  max-width: 1400px;
  margin: 0 auto;
}
</style>
